"use client";
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useAnimation, useInView } from 'framer-motion';
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { galleryService, categoryToTranslationKey, type GalleryItem } from '../../services/galleryService';

// 转换后的图片数据接口（保持原有格式兼容性）
interface TransformedGalleryImage {
  id: string;
  image: string;
  subjectKey: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
}

export function Gallery() { 
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const isInView = useInView(sectionRef, { once: false, amount: 0.1 });
  const { t, i18n } = useTranslation();
  
  // 数据状态
  const [galleryImages, setGalleryImages] = useState<TransformedGalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Lightbox 状态
  const [open, setOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  
  // 自动滚动状态
  const [autoScroll, setAutoScroll] = useState(true);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 从Firebase获取gallery数据
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const galleryData = await galleryService.getActiveGalleryImages();
        
        // 转换数据格式以保持兼容性
        const transformedData: TransformedGalleryImage[] = galleryData.map((item: GalleryItem) => ({
          id: item.id,
          image: item.image,
          subjectKey: categoryToTranslationKey[item.category] || 'gallery.images.default',
          title: item.title,
          titleEn: item.titleEn,
          description: item.description,
          descriptionEn: item.descriptionEn
        }));
        
        setGalleryImages(transformedData);
      } catch (err) {
        console.error('获取gallery数据失败:', err);
        setError('加载图片数据失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  // 格式化 Lightbox 所需的图片数据
  const slides = galleryImages.map(item => ({
    src: item.image,
    title: i18n.language === 'en' ? item.titleEn : item.title,
    description: i18n.language === 'en' ? item.descriptionEn : item.description
  }));
  
  // 手动滚动逻辑
  const scroll = useCallback((direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.33; // 滚动视窗宽度的三分之一
      const scrollLeft = scrollRef.current.scrollLeft;
      
      scrollRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);
  
  // 启动自动滚动
  const startAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
    }
    
    autoScrollIntervalRef.current = setInterval(() => {
      if (scrollRef.current) {
        const scrollWidth = scrollRef.current.scrollWidth;
        const clientWidth = scrollRef.current.clientWidth;
        const scrollLeft = scrollRef.current.scrollLeft;
        
        // 如果已经滚动到最右边，则回到开始位置
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          // 否则继续向右滚动
          scroll('right');
        }
      }
    }, 4000); // 每4秒滚动一次
  }, [scroll]);
  
  // 停止自动滚动
  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);
  
  // 当组件进入视图时或数据加载完成时启动动画
  useEffect(() => {
    if (isInView && !loading) {
      controls.start("visible");
    }
  }, [controls, isInView, loading]);

  // 数据加载完成后，如果组件已经在视图中，立即启动动画
  useEffect(() => {
    if (!loading && galleryImages.length > 0) {
      // 延迟一点时间确保DOM已经渲染
      const timer = setTimeout(() => {
        controls.start("visible");
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, galleryImages.length, controls]);
  
  // 自动滚动控制
  useEffect(() => {
    if (autoScroll) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }
    
    return () => stopAutoScroll();
  }, [autoScroll, startAutoScroll, stopAutoScroll]);
  
  // 处理图片点击
  const handleImageClick = (index: number) => {
    setImageIndex(index);
    setOpen(true);
    setAutoScroll(false); // 打开预览时暂停自动滚动
  };
  
  // 关闭预览后恢复自动滚动
  const handleCloseLightbox = () => {
    setOpen(false);
    setAutoScroll(true); // 关闭预览后恢复自动滚动
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.8,
        staggerChildren: 0.2 
      }
    }
  };
  
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <section 
        className="w-full py-24 relative overflow-hidden bg-gradient-to-b from-white to-gray-50" 
        ref={sectionRef}
      >
        {/* 装饰元素 - 背景几何图形 */}
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-700"></div>
          <div className="absolute top-40 -left-20 w-64 h-64 rounded-full bg-purple-600"></div>
          <div className="absolute bottom-20 right-40 w-80 h-80 rounded-full bg-indigo-600"></div>
        </div>
        
        <div className="max-w-[1920px] mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-xl text-gray-600">loading...</p>
          </div>
        </div>
      </section>
    );
  }

  // 如果有错误，显示错误信息
  if (error) {
    return (
      <section 
        className="w-full py-24 relative overflow-hidden bg-gradient-to-b from-white to-gray-50" 
        ref={sectionRef}
      >
        <div className="max-w-[1920px] mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">加载失败</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              重新加载
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="w-full py-24 relative overflow-hidden bg-gradient-to-b from-white to-gray-50" 
      ref={sectionRef}
    >
      {/* 装饰元素 - 背景几何图形 */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-700"></div>
        <div className="absolute top-40 -left-20 w-64 h-64 rounded-full bg-purple-600"></div>
        <div className="absolute bottom-20 right-40 w-80 h-80 rounded-full bg-indigo-600"></div>
      </div>
      
      <div className="max-w-[1920px] mx-auto px-4 relative z-10">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="mb-16 text-center"
        >
          {/* 精致标题设计 */}
          <motion.div 
            className="inline-block mb-4" 
            variants={titleVariants}
          >
            <p className="text-sm font-medium tracking-widest text-indigo-600 uppercase">
              {t('gallery.subtitle')}
            </p>
          </motion.div>
          
          <motion.h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 max-w-4xl mx-auto leading-tight"
            variants={titleVariants}
          >
            <span className="inline-block relative">
              <span className="relative z-10">{t('gallery.title')}</span>
              <span className="absolute bottom-2 left-0 w-full h-3 bg-indigo-100 -z-10 transform -rotate-1"></span>
            </span>
          </motion.h2>
          
        </motion.div>
        
        {/* 图片画廊容器 */}
        <div className="relative w-full mt-12">
          {/* 滚动控制按钮 */}
          <div className="flex justify-between absolute w-full top-1/2 -translate-y-1/2 z-30 px-4 pointer-events-none">
            <button 
              onClick={() => {
                scroll('left');
                setAutoScroll(false);
                // 用户交互后5秒恢复自动滚动
                setTimeout(() => setAutoScroll(true), 5000);
              }}
              className="bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-300 focus:outline-none transform hover:scale-105 pointer-events-auto"
              aria-label={t('gallery.controls.scrollLeft')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            
            <button 
              onClick={() => {
                scroll('right');
                setAutoScroll(false);
                // 用户交互后5秒恢复自动滚动
                setTimeout(() => setAutoScroll(true), 5000);
              }}
              className="bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-300 focus:outline-none transform hover:scale-105 pointer-events-auto"
              aria-label={t('gallery.controls.scrollRight')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
          
          {/* 自动滚动控制 */}
          <div className="absolute bottom-2 right-6 z-30">
            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className="bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-300 focus:outline-none"
              aria-label={autoScroll ? t('gallery.controls.pause') : t('gallery.controls.play')}
            >
              {autoScroll ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347c-.75.413-1.667-.13-1.667-.986V5.653z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* 图片画廊 */}
          <div 
            ref={scrollRef}
            className="flex gap-6 md:gap-8 overflow-x-auto py-12 px-4 scrollbar-hide"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
            onMouseEnter={() => setAutoScroll(false)}
            onMouseLeave={() => setAutoScroll(true)}
          >
            {galleryImages.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="relative flex-shrink-0 cursor-pointer group"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => handleImageClick(index)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={controls}
                  variants={{
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.5, delay: index * 0.1 }
                    }
                  }}
                >
                  <div className="w-72 h-80 md:w-80 md:h-96 overflow-hidden rounded-xl shadow-xl relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-70 transition-opacity group-hover:opacity-60 z-10"></div>
                    <img
                      src={item.image}
                      alt={i18n.language === 'en' ? item.titleEn : item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    {/* <div className="absolute bottom-0 left-0 w-full p-6 text-white z-20">
                      <h3 className="text-xl font-bold">{t(`${item.subjectKey}.title`)}</h3>
                      <p className="text-sm mt-2 text-gray-200 opacity-0 transform translate-y-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                        {t(`${item.subjectKey}.description`)}
                      </p>
                    </div> */}
                    <div className="absolute top-4 right-4 z-20 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 transform scale-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                      </svg>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </div>
      
      {/* 集成 Lightbox */}
      <Lightbox
        open={open}
        close={handleCloseLightbox}
        slides={slides}
        index={imageIndex}
        plugins={[Zoom, Captions, Thumbnails]}
        captions={{ descriptionTextAlign: "center" }}
        thumbnails={{ position: "bottom" }}
        styles={{ 
          container: { backgroundColor: "rgba(0, 0, 0, .9)" },
          thumbnailsContainer: { backgroundColor: "rgba(0, 0, 0, .7)" }
        }}
        render={{
          iconPrev: () => (
            <span aria-label={t('gallery.lightbox.previous')}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </span>
          ),
          iconNext: () => (
            <span aria-label={t('gallery.lightbox.next')}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </span>
          ),
          iconClose: () => (
            <span aria-label={t('gallery.lightbox.close')}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )
        }}
      />
      
      {/* 定义全局样式 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* 隐藏滚动条 */
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `
      }} />
    </section>
  );
}